import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiAbstractStateComponent } from '@fi-shared/fi-state-proxy/fi-abstract-state.component';
import { FiQuizListComponent } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-list/fi-quiz-list.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiQuizEditComponent } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-edit/fi-quiz-edit.component';
import { FiQuizHelperService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-helper.service';
import { FiQuizService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz.service';
import { FiQuizEditorStoreService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-edit/fi-quiz-editor-store.service';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import {
    AccessRightFilterCollectionModel,
    IAccessRightSelectionFiltersCollection,
} from '@fi-app/shared/models/access-right-filter-collection.model';
import { AccessRightService } from '@fi-app/shared/fi-shared-services';
import { ObjectTypes } from '@fi-app/shared/types/object-type';
import { ModuleType } from '@fi-app/shared/types/module-type';
import { FiQuizViewComponent } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-view/fi-quiz-view.component';

export const onQuizEditorStateExit = (transition: Transition) => {
    const injector = transition.injector();
    const fiQuizEditorStoreService: FiQuizEditorStoreService = injector.get(FiQuizEditorStoreService);
    fiQuizEditorStoreService.resetQuizEditorStoreData();
};

const quizSavedQuickSelectionFiltersResolveFn = [
    AccessRightService,
    FiQuizEditorStoreService,
    (accessRightService: AccessRightService, fiQuizEditorStoreService: FiQuizEditorStoreService) => {
        return accessRightService
            .GetSelectionFiltersForPublication(ModuleType.Onboarding, true)
            .then((result: IAccessRightSelectionFiltersCollection[]) => {
                fiQuizEditorStoreService.setSavedSelectionFilters(result);
                return result;
            });
    },
];

const quizAccessRightFilterCollectionResolveFn = [
    AccessRightService,
    FiQuizEditorStoreService,
    (accessRightService: AccessRightService, fiQuizEditorStoreService: FiQuizEditorStoreService) => {
        return accessRightService
            .getAccessRightsForPublicationByObjectType(ObjectTypes.Quiz)
            .then((result: AccessRightFilterCollectionModel) => {
                fiQuizEditorStoreService.setAccessRightFilterCollection(result);
                return result;
            });
    },
];

const quizSelectedFiltersCollection = [
    Transition,
    FiQuizHelperService,
    (tr: Transition, fiQuizHelperService: FiQuizHelperService) => {
        return Promise.all([
            tr.injector().getAsync('quiz'),
            tr.injector().getAsync('savedQuickSelectionFilters'),
            tr.injector().getAsync('accessRightFilterCollection'),
        ]).then(() => {
            fiQuizHelperService.setInitialSavedQuickSelectionFilters();
        });
    },
];

const newQuizResolveFn = [
    FiQuizHelperService,
    FiQuizEditorStoreService,
    (fiQuizHelperService: FiQuizHelperService, fiQuizEditorStoreService: FiQuizEditorStoreService) => {
        return new Promise((resolve) => {
            const quiz = fiQuizEditorStoreService.currentQuizData();

            if (isNullOrUndefined(quiz)) {
                let newQuiz = fiQuizHelperService.getNewQuizInstance();
                newQuiz = fiQuizHelperService.initContentLanguages(newQuiz);
                fiQuizEditorStoreService.setInitialQuizData(newQuiz);
                fiQuizEditorStoreService.setCurrentQuizData(newQuiz);
                fiQuizHelperService.setInitialSelectedLanguageId(newQuiz);
                resolve(newQuiz);
            }

            resolve(quiz);
        });
    },
];

const copyQuizResolveFn = [
    FiQuizEditorStoreService,
    FiQuizHelperService,
    FiQuizService,
    FiStateService,
    Transition,
    (
        fiQuizEditorStoreService: FiQuizEditorStoreService,
        fiQuizHelperService: FiQuizHelperService,
        fiQuizService: FiQuizService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        const quizId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiQuizService.getQuizCopy(quizId).then((quizCopy) => {
            quizCopy = fiQuizHelperService.initContentLanguages(quizCopy);
            quizCopy.Names = fiQuizHelperService.insertCopyLabelIntoContentLanguageLabelModels(quizCopy.Names);

            fiQuizEditorStoreService.setInitialQuizData(quizCopy);
            fiQuizEditorStoreService.setCurrentQuizData(quizCopy);
            fiQuizHelperService.setInitialSelectedLanguageId(quizCopy);
            return quizCopy;
        });
    },
];

const editQuizResolveFn = [
    FiQuizEditorStoreService,
    FiQuizHelperService,
    FiQuizService,
    FiStateService,
    Transition,
    (
        fiQuizEditorStoreService: FiQuizEditorStoreService,
        fiQuizHelperService: FiQuizHelperService,
        fiQuizService: FiQuizService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        const quizId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiQuizService.getQuiz(quizId).then((quiz) => {
            quiz = fiQuizHelperService.initContentLanguages(quiz);
            fiQuizEditorStoreService.setInitialQuizData(quiz);
            fiQuizEditorStoreService.setCurrentQuizData(quiz);
            fiQuizHelperService.setInitialSelectedLanguageId(quiz);
            return quiz;
        });
    },
];

const viewQuizResolveFn = [
    FiQuizService,
    FiStateService,
    Transition,
    (fiQuizService: FiQuizService, fiStateService: FiStateService, tr: Transition) => {
        const quizId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiQuizService.getQuizViewer(quizId);
    },
];

export const QuizState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz._name,
    url: '/Quiz',
    abstract: true,
    component: FiAbstractStateComponent,
};

export const QuizListState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiQuizListComponent,
    reloadOnSearch: false,
};

export const QuizAddState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiQuizEditComponent,
        },
    },
    resolve: {
        quiz: newQuizResolveFn,
        accessRightFilterCollection: quizAccessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: quizSavedQuickSelectionFiltersResolveFn,
    },
    onExit: onQuizEditorStateExit,
};

export const QuizEditState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz.Edit,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiQuizEditComponent,
        },
    },
    resolve: {
        quiz: editQuizResolveFn,
        accessRightFilterCollection: quizAccessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: quizSavedQuickSelectionFiltersResolveFn,
        selectedFiltersCollection: quizSelectedFiltersCollection,
    },
    onExit: onQuizEditorStateExit,
};

export const QuizViewState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz.View,
    url: '/View/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiQuizViewComponent,
        },
    },
    resolve: {
        quizViewer: viewQuizResolveFn,
    },
};

export const QuizCopyState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Quiz.Copy,
    url: '/Copy/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiQuizEditComponent,
        },
    },
    resolve: {
        isCopyMode: [() => true],
        quiz: copyQuizResolveFn,
        accessRightFilterCollection: quizAccessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: quizSavedQuickSelectionFiltersResolveFn,
    },
    onExit: onQuizEditorStateExit,
};

export const QuizStates: StatesModule = {
    states: [QuizState, QuizListState, QuizAddState, QuizEditState, QuizCopyState, QuizViewState],
};
